<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("BUTTONS.add_notification") }}</h4>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Start:: Receiver Type Input -->
          <base-select-input
            col="6"
            :optionsList="receiverTypes"
            :placeholder="$t('PLACEHOLDERS.receiverType')"
            v-model="data.receiverTypes"
            @input="handleTypeChange"
            multiple
            required
          />
          <!-- End:: Receiver Type Input -->

          <!-- Start:: Send To All Checkbox -->
          <base-switch-input
            v-if="
              data.receiverTypes && data.receiverTypes.length > 0 && !hasAllType
            "
            col="6"
            :label="$t('PLACEHOLDERS.send_to_all')"
            v-model="data.to_all"
          />
          <!-- End:: Send To All Checkbox -->

          <!-- Start:: Users List Input -->
          <base-select-input
            v-if="
              !data.to_all &&
              !hasAllType &&
              data.receiverTypes &&
              data.receiverTypes.length > 0 &&
              availableUsers.length > 0
            "
            col="6"
            :optionsList="availableUsers"
            :placeholder="getUsersPlaceholder"
            v-model="data.selectedUsers"
            :loading="isLoadingUsers"
            multiple
            required
          />
          <!-- End:: Users List Input -->

          <div class="col-12">
            <div class="row">
              <!-- Start:: Title Ar Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.titleAr')"
                v-model.trim="data.titleAr"
                required
              />
              <!-- End:: Title Ar Input -->

              <!-- Start:: Title En Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.titleEn')"
                v-model.trim="data.titleEn"
                required
              />
              <!-- End:: Title En Input -->

              <!-- Start:: Content Ar Input -->
              <base-input
                col="6"
                rows="7"
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.contentAr')"
                v-model.trim="data.contentAr"
                required
              />
              <!-- End:: Content Ar Input -->

              <!-- Start:: Content En Input -->
              <base-input
                col="6"
                rows="7"
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.contentEn')"
                v-model.trim="data.contentEn"
                required
              />
              <!-- End:: Content En Input -->
            </div>
          </div>

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "CreateNotification",

  computed: {
    // Start:: Vuex Getters
    ...mapGetters({
      allClientsData: "ApiGetsModule/allClientsData",
      allDriversData: "ApiGetsModule/allDriversData",
    }),
    // End:: Vuex Getters

    receiverTypes() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.drivers"),
          value: "driver",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.providers"),
          value: "provider",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.distribution_points"),
          value: "Distribution point",
        },
        {
          id: 4,
          name: this.$t("PLACEHOLDERS.all"),
          value: "all",
        },
      ];
    },

    getUsersPlaceholder() {
      if (!this.data.receiverTypes || this.data.receiverTypes.length === 0)
        return "";

      return this.$t("PLACEHOLDERS.select_users");
    },

    hasAllType() {
      if (!this.data.receiverTypes || this.data.receiverTypes.length === 0)
        return false;
      return this.data.receiverTypes.some((type) => type.value === "all");
    },
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      isLoadingUsers: false,
      // End:: Loader Control Data

      // Start:: Available Users List
      availableUsers: [],
      // End:: Available Users List

      // Start:: Data Collection To Send
      data: {
        receiverTypes: [],
        to_all: false,
        selectedUsers: [],
        titleAr: null,
        titleEn: null,
        contentAr: null,
        contentEn: null,
      },

      // End:: Data Collection To Send
    };
  },
  watch: {
    "data.to_all"(newVal) {
      if (newVal) {
        // Clear selected users when "send to all" is enabled
        this.data.selectedUsers = [];
      }
    },
  },

  methods: {
    // Start:: Handle Type Change
    handleTypeChange() {
      // Reset users when types change
      this.data.selectedUsers = [];
      this.availableUsers = [];
      this.data.to_all = false;

      // If "all" is selected, clear other selections
      if (this.hasAllType) {
        this.data.receiverTypes = this.data.receiverTypes.filter(
          (type) => type.value === "all"
        );
        return;
      }

      // Fetch users if not "all" type
      if (this.data.receiverTypes && this.data.receiverTypes.length > 0) {
        this.fetchAvailableUsers();
      }
    },
    // End:: Handle Type Change

    // Start:: Fetch Available Users Based On Type
    async fetchAvailableUsers() {
      if (
        !this.data.receiverTypes ||
        this.data.receiverTypes.length === 0 ||
        this.hasAllType
      ) {
        return;
      }

      this.isLoadingUsers = true;
      this.availableUsers = [];

      try {
        // Fetch users for each selected type
        const promises = this.data.receiverTypes.map(async (type) => {
          let res = await this.$axios({
            method: "GET",
            url: `notification/get-notifables/${type.value}`,
          });
          return Object.values(res.data).filter((item) => item.user);
        });

        const results = await Promise.all(promises);

        // Combine all users from different types
        const allUsers = results.flat();

        // Remove duplicates based on user id
        const uniqueUsers = allUsers.reduce((acc, item) => {
          if (!acc.find((u) => u.user.id === item.user.id)) {
            acc.push(item);
          }
          return acc;
        }, []);

        // Transform to select options format
        this.availableUsers = uniqueUsers.map((item) => ({
          id: item.user.id,
          name: item.user.name,
        }));

        this.isLoadingUsers = false;
      } catch (error) {
        this.isLoadingUsers = false;
        console.log(error.response?.data?.message);
        this.$message.error(this.$t("MESSAGES.errorOccurred"));
      }
    },
    // End:: Fetch Available Users Based On Type

    // Start:: validate Form Inputs
    validateFormInputs() {
      this.isWaitingRequest = true;

      if (!this.data.receiverTypes || this.data.receiverTypes.length === 0) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.receiverType"));
        return;
      } else if (
        !this.data.to_all &&
        !this.hasAllType &&
        this.data.selectedUsers.length === 0
      ) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.selectAtLeastOneUser"));
        return;
      } else if (!this.data.titleAr) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.titleAr"));
        return;
      } else if (!this.data.titleEn) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.titleEn"));
        return;
      } else if (!this.data.contentAr) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.contentAr"));
        return;
      } else if (!this.data.contentEn) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.contentEn"));
        return;
      } else {
        this.submitForm();
        return;
      }
    },
    // End:: validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();

      // Start:: Append Request Data
      REQUEST_DATA.append("title_ar", this.data.titleAr);
      REQUEST_DATA.append("title_en", this.data.titleEn);
      REQUEST_DATA.append("body_ar", this.data.contentAr);
      REQUEST_DATA.append("body_en", this.data.contentEn);

      // Add types as array
      this.data.receiverTypes.forEach((type) => {
        REQUEST_DATA.append("types[]", type.value);
      });

      // Add to_all flag
      if (this.hasAllType) {
        REQUEST_DATA.append("to_all", 1);
      } else {
        REQUEST_DATA.append("to_all", this.data.to_all ? 1 : 0);

        // Add selected user IDs if not sending to all
        if (!this.data.to_all) {
          this.data.selectedUsers.forEach((user) => {
            REQUEST_DATA.append("ids[]", user.id);
          });
        }
      }
      // End:: Append Request Data

      try {
        await this.$axios({
          method: "POST",
          url: `notification/store`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.sentSuccessfully"));
        this.$router.push({ path: "/all-notifications/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Submit Form
  },

  mounted() {
    // No initial data loading needed
  },
};
</script>
